import React from 'react'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import styled from 'styled-components'
import { Helmet } from 'react-helmet'
import { css } from '@styled-system/css'

import {
  Box,
  FormGroup,
  Paragraph,
  P,
  H1,
  H2,
  Tile,
  IconContainer,
} from '@allied-solutions/affinity'
import {
  ShieldConfirm,
  DocumentCheck02,
  Sign01,
} from '@allied-solutions/affinity-react-icons/dist/standard'
import { useFetchBorrower, useStore } from '../../hooks'
import { AppLayout, Layout, NextButton } from '../../components'
import illustration from './Illustration.svg'
import { LinkAnchor } from '../../components/LinkAnchor'

const StyledTile = styled(Tile)`
  border-radius: ${props => props.theme.radii.xl};
`

const SubmitInsurancePage = props => {
  const { state } = useStore()
  const { handleSubmit } = useFetchBorrower(
    '/enter-reference-id/confirm-identity/'
  )

  return (
    <Layout>
      <Helmet>
        <title>Verify My Insurance</title>
        <meta
          name="description"
          content="Start the 5-minute insurance verification process on your own by entering your reference ID number from the letter or email you received."
        />
      </Helmet>
      <Formik
        initialValues={{
          refId: '',
          firstName: '',
          lastName: '',
          ...state.form,
        }}
        onSubmit={handleSubmit}
        validationSchema={Yup.object().shape({
          refId: Yup.string().required('Please enter a valid Reference ID.'),
        })}
      >
        {props => (
          <main>
            <Form noValidate>
              <AppLayout
                leftSide={
                  // <Box mt={[null, '3rem', null, null]}>
                  <>
                    <Paragraph
                      mb={6}
                      typeStyle="labelLarge"
                      textAlign="left"
                      color="gray600"
                    >
                      Getting Started
                    </Paragraph>
                    <H1 className="h5" typeStyle="h5">
                      Enter your reference ID.
                    </H1>
                    <Paragraph
                      typeStyle="bodyMedium"
                      textAlign="left"
                      color="gray600"
                      mb={8}
                    >
                      Your reference ID can be found in the email or letter your
                      financial institution sent you.
                    </Paragraph>

                    <StyledTile maxWidth={[null, '410px', null, null]}>
                      <Tile.Header>
                        <H2 typeStyle="titleSmall.bold" m="0">
                          Before you start
                        </H2>
                      </Tile.Header>
                      <Box
                        display="flex"
                        gap={8}
                        flexDirection="column"
                        color="text.secondary"
                        pr={[2, 0, null, null]}
                      >
                        <Box display="flex" gap={3}>
                          <IconContainer icon={ShieldConfirm} />
                          <P typeStyle="bodyMedium" mb="0">
                            You must obtain insurance through your preferred
                            provider. <i>We do not sell insurance.</i>
                          </P>
                        </Box>
                        <Box display="flex" gap={3}>
                          <IconContainer icon={DocumentCheck02} />
                          <P typeStyle="bodyMedium" mb="0">
                            Have insurance documents ready to upload for review.
                          </P>
                        </Box>
                        <Box display="flex" gap={3}>
                          <IconContainer icon={Sign01} />
                          <P typeStyle="bodyMedium" mb="0">
                            We will tell you what information to provide through
                            a stepped process.
                          </P>
                        </Box>
                      </Box>
                    </StyledTile>

                    <FormGroup id="refId" required mt={8} mb={5} focusStyleTest>
                      <FormGroup.Label visible>Reference ID</FormGroup.Label>
                      <FormGroup.InputGroup tabIndex={-1}>
                        <FormGroup.InputGroup.Input
                          tabIndex={0}
                          pattern="[0-9]*"
                        />
                      </FormGroup.InputGroup>
                      <FormGroup.Caption />
                    </FormGroup>

                    {state.hasSessionToken === true && (
                      <Box
                        display={[null, 'flex', 'block', 'block', 'flex']}
                        justifyContent="space-between"
                      >
                        <FormGroup
                          id="firstName"
                          width={[null, '48%', '100%', '100%', '48%']}
                          mb={['5', 0]}
                        >
                          <FormGroup.Label visible>First Name</FormGroup.Label>
                          <FormGroup.InputGroup>
                            <FormGroup.InputGroup.Input />
                          </FormGroup.InputGroup>
                          <FormGroup.Caption />
                        </FormGroup>

                        <FormGroup
                          id="lastName"
                          width={[null, '48%', '100%', '100%', '48%']}
                          mb={0}
                        >
                          <FormGroup.Label visible>Last Name</FormGroup.Label>
                          <FormGroup.InputGroup>
                            <FormGroup.InputGroup.Input guide={false} />
                          </FormGroup.InputGroup>
                          <FormGroup.Caption />
                        </FormGroup>
                      </Box>
                    )}
                    {/* </Box> */}
                  </>
                }
                rightSide={
                  <img
                    src={illustration}
                    alt=""
                    css={css({
                      display: ['none', null, null, 'block'],
                      mx: 'auto',
                    })}
                  />
                }
                footerButtons={
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <LinkAnchor
                      id="EnterRefId__Button--noRefId"
                      to="/no-reference-id/"
                    >
                      <P mb={0} color="text.secondary" typeStyle="buttonMedium">
                        Don't have an ID?
                      </P>
                    </LinkAnchor>
                    <NextButton
                      tabIndex="0"
                      formikProps={props}
                      id="EnterRefId__Button--next"
                    >
                      Get Started
                    </NextButton>
                  </Box>
                }
              />
            </Form>
          </main>
        )}
      </Formik>
    </Layout>
  )
}

export default SubmitInsurancePage
